import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'next-i18next';

import styles from './index-details-popin.module.scss';
import { Popin } from '@utmb/design-system/popin/Popin';
import { Font } from '@utmb/design-system/text/font';
import { Icon } from '@utmb/design-system/icons/Icon';
import { IconsEnum } from '@utmb/types/icons';
import { PiCategoryLogo } from '@utmb/design-system/pi-category-logo/PiCategoryLogo';
import { IndexDetailRow } from './index-detail-row/index-detail-row';
import { AverageIndex } from './average-index/average-index';
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import { useUser } from '@utmb/contexts/AuthContext';
import { calculateIndexBreakdown } from '@utmb/utils/index-calculation';
import RaceYearResultForRunnerView from '@utmb/types/view/RaceYearResultForRunnerView';

export interface IndexDetailsPopinProps {
    results: RaceYearResultForRunnerView[];
    category: PiCategoryEnum;
    isAuthenticated: boolean;
    close: () => void;
    score?: number;
}

export function IndexDetailsPopin({ results, category, isAuthenticated, close, score }: IndexDetailsPopinProps): ReactElement {
    const { t } = useTranslation();
    const { login } = useUser();

    const breakdown = useMemo(() => calculateIndexBreakdown(results, category), [results, category]);

    const { rows } = breakdown;
    const hasResults = rows.some((row) => !row.isFiller);

    // Best (top) adjusted score: reference used to compute every degraded best score.
    const bestScore = rows[0]?.adjustedScore ?? 0;

    const columns: { label: string; withHelp?: boolean }[] = isAuthenticated
        ? [
              { label: t('singleRunner.statistics.indexDetail.fiveBestPerf') },
              { label: t('singleRunner.statistics.indexDetail.scoreObtained') },
              { label: t('singleRunner.statistics.indexDetail.date') },
              { label: t('singleRunner.statistics.indexDetail.adjustedScore') },
              { label: t('singleRunner.statistics.indexDetail.guaranteedScore'), withHelp: true },
              { label: t('singleRunner.statistics.indexDetail.retained') },
          ]
        : [{ label: t('singleRunner.statistics.indexDetail.fiveBestPerf') }, { label: t('singleRunner.statistics.indexDetail.scoreObtained') }, { label: t('singleRunner.statistics.indexDetail.date') }];

    const gridClass = isAuthenticated ? '' : styles.notAuthenticated;

    return (
        <Popin closeCallback={close} displayBorderRadius glassBorder>
            <div className={styles.indexDetailsPopin}>
                <div className={styles.titleWrap}>
                    <div className={styles.wrapScore}>
                        <PiCategoryLogo height={30} piCategory={category} isDark={false} />
                    </div>
                    <Font.FuturaMedium className={styles.label} desktop="48" mobile="18">
                        {t('singleRunner.statistics.indexDetail.title')}
                    </Font.FuturaMedium>
                </div>

                {!hasResults ? (
                    !isAuthenticated ? (
                        <button type="button" className={styles.loginButton} onClick={login}>
                            <Font.OxaniumRegular desktop="16" mobile="14">
                                {t('singleRunner.resultsTable.connectSeeIndex')}
                            </Font.OxaniumRegular>
                        </button>
                    ) : (
                        <Font.FuturaBook className={styles.empty} desktop="16" mobile="14">
                            {t('racesList.noResults')}
                        </Font.FuturaBook>
                    )
                ) : (
                    <div className={`${styles.wrapData} ${gridClass}`}>
                        <div className={`${styles.header} ${gridClass}`}>
                            {columns.map(({ label, withHelp }) => (
                                <Font.FuturaBook key={label} as="span" desktop="18" mobile="16">
                                    {withHelp ? (
                                        <span className={styles.helpLabel}>
                                            {label}
                                            <span className={styles.helpIcon}>
                                                <Icon variant={IconsEnum.HELP} width={20} height={20} />
                                                <span className={styles.tooltip} role="tooltip">
                                                    <Font.FuturaMedium as="span" desktop="16" mobile="14">
                                                        {t('singleRunner.statistics.indexDetail.guaranteedScore')}
                                                    </Font.FuturaMedium>
                                                    <Font.FuturaBook as="span" desktop="14" mobile="12">
                                                        {t('singleRunner.statistics.indexDetail.guaranteedScoreExplanation')}
                                                    </Font.FuturaBook>
                                                </span>
                                            </span>
                                        </span>
                                    ) : (
                                        label
                                    )}
                                </Font.FuturaBook>
                            ))}
                        </div>

                        <div className={styles.table}>
                            {rows.map((row, i) => (
                                <IndexDetailRow
                                    key={row.result?.raceYearId ?? `filler-${i}`}
                                    row={row}
                                    position={i + 1}
                                    bestScore={bestScore}
                                    isAuthenticated={isAuthenticated}
                                    login={login}
                                />
                            ))}
                        </div>

                        {isAuthenticated && (
                            <div className={styles.wrapAverage}>
                                <AverageIndex index={score ?? null} />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Popin>
    );
}
